import permissions from '@enums/user-permissions';
import { getWorkpackageScenarioFromRoute } from '@/utils/scenario-from-route';

const ScenarioWorkflow = () => import('@/pages/scenario/workflow/ScenarioWorkflow.vue');

const Admin = () => import('@/pages/admin/AdminPage.vue');
const ClientAdmin = () => import('@/pages/client-admin/ClientAdminPage.vue');

// Furniture and space
const ScenarioSpace = () => import('@/pages/scenario/space/ScenarioSpace.vue');
const FurnitureEditor = () => import('@/pages/scenario/space/furniture-editor/FurnitureEditor.vue');
const FurnitureMapping = () =>
  import('@/pages/scenario/space/furniture-mapping/FurnitureMapping.vue');
const SpacebreakCalculator = () =>
  import('@/pages/scenario/space/spacebreak-calculator/SpacebreakCalculator.vue');
const StoreclassDefinition = () =>
  import('@/pages/scenario/space/storeclass-definition/StoreclassDefinition.vue');
const PlanogramSelection = () =>
  import('@/pages/scenario/space/planogram-selection/PlanogramSelection.vue');

// Inputs
const ScenarioInput = () => import('@/pages/scenario/input/ScenarioInput.vue');
const AttributeEditor = () => import('@/pages/scenario/input/attribute-editor/AttributeEditor.vue');
const CannGroups = () => import('@/pages/scenario/input/cann-groups/CannGroups.vue');
const VarietyGroups = () => import('@/pages/scenario/input/variety-groups/VarietyGroups.vue');

// Measuring
const ScenarioMeasuring = () => import('@/pages/scenario/measuring/ScenarioMeasuring.vue');
const SwitchingWrapper = () =>
  import('@/pages/scenario/measuring/switching-behaviour/SwitchingWrapper.vue');
const CustomerDecisionTree = () =>
  import('@/pages/scenario/measuring/customer-decision-tree/CustomerDecisionTree.vue');
const Region = () => import('@/pages/scenario/measuring/region/Region.vue');
const Clustering = () => import('@/pages/scenario/measuring/clustering/Clustering.vue');

// Modelling
const ScenarioModelling = () => import('@/pages/scenario/modelling/ScenarioModelling.vue');
const ProductModellingPage = () =>
  import('@/pages/scenario/modelling/product-modelling/ProductModellingPage.vue');
const SwitchingModelling = () =>
  import('@/pages/scenario/modelling/switching-modelling/SwitchingModelling.vue');
const ProductModellingDetail = () =>
  import('@/pages/scenario/modelling/product-modelling/ProductModellingDetail.vue');

// Swaps
const ScenarioSwaps = () => import('@/pages/scenario/swaps/ScenarioSwaps.vue');
const ProductsToSwap = () => import('@/pages/scenario/swaps/ProductsToSwap.vue');
const PrepareSwaps = () => import('@/pages/scenario/swaps/PrepareSwaps.vue');

// Extracts
const ScenarioExtracts = () => import('@/pages/scenario/extracts/ScenarioExtracts.vue');

// Assortments
const AssortmentPanel = () =>
  import('@/pages/home/workpackage-scenarios/scenario-panel/ScenarioAssortmentsPanel.vue');
const ScenarioAssortments = () => import('@/pages/scenario/assortments/ScenarioAssortments.vue');

// Execution
const ScenarioExecution = () => import('@/pages/scenario/execution/ScenarioExecution.vue');
const JdaStepper = () => import('@/pages/scenario/execution/extract/jda/JdaStepper.vue');
const FurnitureExtract = () =>
  import('@/pages/scenario/execution/extract/furniture/FurnitureExtract.vue');
const ReportProductStoreExtract = () =>
  import('@/pages/scenario/execution/extract/product-store-extract/ReportProductStoreExtract.vue');
const Finalisation = () =>
  import('@/pages/scenario/execution/extract/finalisation/Finalisation.vue');

const executionRoute = {
  path: 'execution',
  name: 'execution',
  component: ScenarioExecution,
  omitFromNav: true,
  meta: {
    requiresPermission: permissions.canViewExecutionPage,
    titleExtension: 'toolbar.executionTab',
  },
  children: [
    {
      path: 'product-store-extract',
      name: 'product-store-extract',
      component: ReportProductStoreExtract,
      omitFromNav: true,
      meta: {
        requiresPermission: permissions.canViewExtractsPage,
        titleExtension: 'scenarioExecutionPage.tab.productExtract',
      },
    },
  ],
};

executionRoute.children.push({
  path: 'finalisation',
  name: 'finalisation',
  component: Finalisation,
  omitFromNav: true,
  meta: {
    featureKey: 'finalisationEnabled',
    requiresPermission: permissions.canViewFinalisation,
    titleExtension: 'scenarioExecutionPage.tab.finalisation',
  },
});

executionRoute.children.push({
  path: 'jda',
  name: 'jda',
  component: JdaStepper,
  omitFromNav: true,
  meta: {
    featureKey: 'jdaExtractEnabled',
    titleExtension: 'scenarioExecutionPage.tab.storeExecutionPlanning',
  },
});

executionRoute.children.push({
  path: 'furniture',
  name: 'furniture',
  component: FurnitureExtract,
  omitFromNav: true,
  meta: {
    featureKey: 'furnitureExtractEnabled',
    requiresPermission: permissions.canViewExtractsPage,
    titleExtension: 'scenarioExecutionPage.tab.furnitureExtract',
  },
});

// One entry per scenario; the workflow steps under it collapse into that entry.
const recordScenarioInShell = {
  key: route => route.params.scenarioId,
  title: route => {
    const { scenario, workpackage } = getWorkpackageScenarioFromRoute(route);
    const workpackageName = workpackage?.name || workpackage?.description;
    if (!scenario || !workpackageName) return undefined;
    return `${workpackageName} | ${scenario.name}`;
  },
};

const baseRoutes = [
  {
    path: '/scenario/:scenarioId',
    name: 'scenario-workflow',
    id: 'scenario-workflow',
    component: ScenarioWorkflow,
    linkText: 'navBar.scenarioWorkflow',
    meta: {
      titleExtension: 'navBar.scenarioWorkflow',
      recordRecentlyOpenedInShell: recordScenarioInShell,
    },
    omitFromNav: true,
    children: [
      {
        path: 'space',
        name: 'space',
        component: ScenarioSpace,
        omitFromNav: true,
        children: [
          {
            path: 'furniture-editor',
            name: 'furniture-editor',
            component: FurnitureEditor,
            omitFromNav: true,
            meta: {
              // TODO: bring back permissions.canViewExtractsPage
              requiresPermission: permissions.canViewFurnitureEditorPage,
              titleExtension: 'scenarioManagementPanel.furnitureEditor',
            },
          },
          {
            path: 'furniture-mapping',
            name: 'furniture-mapping',
            component: FurnitureMapping,
            omitFromNav: true,
            meta: {
              requiresPermission: permissions.canViewFurnitureMappingPage,
              titleExtension: 'scenarioManagementPanel.storeMapping',
            },
          },
          {
            path: 'spacebreak-calculator',
            name: 'spacebreak-calculator',
            component: SpacebreakCalculator,
            omitFromNav: true,
            meta: {
              requiresPermission: permissions.canViewSpacebreakCalculatorPage,
              titleExtension: 'scenarioManagementPanel.spacebreak',
            },
          },
          {
            path: 'storeclass-definition',
            name: 'storeclass-definition',
            component: StoreclassDefinition,
            omitFromNav: true,
            meta: {
              requiresPermission: permissions.canViewStoreclassDefinitionPage,
              titleExtension: 'scenarioManagementPanel.storeClass',
            },
          },
          {
            path: 'planogram-selection',
            name: 'planogram-selection',
            component: PlanogramSelection,
            omitFromNav: true,
            meta: {
              requiresPermission: permissions.canViewPlanogramSelectionPage,
              titleExtension: 'scenarioManagementPanel.planograms',
            },
          },
        ],
      },
      {
        path: 'inputs',
        name: 'inputs',
        component: ScenarioInput,
        omitFromNav: true,
        children: [
          {
            path: 'attributes',
            name: 'attributes',
            component: AttributeEditor,
            omitFromNav: true,
            meta: {
              requiresPermission: permissions.canViewAttributeEditorPage,
              titleExtension: 'scenarioInputPage.tab.attr',
            },
          },
          {
            path: 'cann-group',
            name: 'cann-group',
            component: CannGroups,
            omitFromNav: true,
            meta: {
              requiresPermission: permissions.canViewCannGroupPage,
              titleExtension: 'scenarioInputPage.tab.cann-group',
            },
          },
          {
            path: 'variety',
            name: 'variety',
            component: VarietyGroups,
            omitFromNav: true,
            meta: {
              requiresPermission: permissions.canViewVarietyPage,
              titleExtension: 'scenarioInputPage.tab.variety',
            },
          },
          {
            path: 'prepare-swaps',
            name: 'prepare-swaps',
            component: PrepareSwaps,
            omitFromNav: true,
            meta: {
              requiresPermission: permissions.canViewPrepareSwapsPage,
              titleExtension: 'scenarioInputPage.tab.prepareSwaps',
            },
          },
        ],
      },
      {
        path: 'measuring',
        name: 'measuring',
        component: ScenarioMeasuring,
        omitFromNav: true,
        children: [
          {
            path: 'switching-behaviour',
            name: 'switching-behaviour',
            component: SwitchingWrapper,
            omitFromNav: true,
            meta: {
              requiresPermission: permissions.canViewSwitchingBehaviourPage,
              titleExtension: 'scenarioInputPage.tab.switchingBehaviour',
            },
          },
          {
            path: 'cdt',
            name: 'cdt',
            component: CustomerDecisionTree,
            omitFromNav: true,
            meta: {
              requiresPermission: permissions.canViewCDTPage,
              titleExtension: 'scenarioInputPage.tab.cdt',
            },
          },
          {
            path: 'region',
            name: 'region',
            component: Region,
            omitFromNav: true,
            meta: {
              featureKey: 'regionsByAttributeEnabled',
              requiresPermission: permissions.canViewClusteringPage, // TODO: fix
              titleExtension: 'scenarioInputPage.tab.region',
            },
          },
          {
            path: 'clustering',
            name: 'clustering',
            component: Clustering,
            omitFromNav: true,
            omitChildrenFromNavbar: true,
            meta: {
              requiresPermission: permissions.canViewClusteringPage,
              titleExtension: 'scenarioInputPage.tab.clustering',
            },
            // clustering is a unique case, clustering.vue handles rending of tabs, but we want router to stay up to date
            children: [
              {
                path: ':clusterSchemeId/comparison',
                name: 'comparison',
                component: Clustering,
                omitFromNav: true,
                meta: {
                  requiresPermission: permissions.canViewClusteringPage,
                  titleExtension: 'clusteringPage.tabs.comparison.title',
                },
              },
              {
                path: ':clusterSchemeId/deviation',
                name: 'deviation',
                component: Clustering,
                omitFromNav: true,
                meta: {
                  requiresPermission: permissions.canViewClusteringPage,
                  titleExtension: 'clusteringPage.tabs.deviation.title',
                },
              },
              {
                path: ':clusterSchemeId/store-allocation-list',
                name: 'store-allocation-list',
                component: Clustering,
                omitFromNav: true,
                meta: {
                  requiresPermission: permissions.canViewClusteringPage,
                  titleExtension: 'clusteringPage.tabs.storeAllocation.title',
                },
              },
              {
                path: ':clusterSchemeId/store-allocation-map',
                name: 'store-allocation-map',
                component: Clustering,
                omitFromNav: true,
                meta: {
                  requiresPermission: permissions.canViewClusteringPage,
                  titleExtension: 'clusteringPage.tabs.storeAllocation.title',
                },
              },
            ],
          },
        ],
      },
      {
        path: 'swaps',
        name: 'swaps',
        component: ScenarioSwaps,
        children: [
          {
            path: 'products-to-swap',
            name: 'products-to-swap',
            component: ProductsToSwap,
            omitFromNav: true,
            meta: {
              requiresPermission: permissions.canViewSwapsPage,
              titleExtension: 'scenarioManagementPanel.productsToSwap',
            },
          },
        ],
        omitFromNav: true,
      },
      {
        path: 'modelling',
        name: 'modelling',
        component: ScenarioModelling,
        omitFromNav: true,
        children: [
          {
            path: 'product-modelling',
            name: 'product-modelling',
            component: ProductModellingPage,
            omitFromNav: true,
            meta: {
              requiresPermission: permissions.canViewModellingPage,
              titleExtension: 'scenarioModellingPage.tab.productModelling',
            },
          },
          {
            path: 'product-modelling/:productKeyDisplay',
            name: 'product-detail',
            component: ProductModellingDetail,
            omitFromNav: true,
            meta: {
              requiresPermission: permissions.canViewModellingPage,
              titleExtension: 'scenarioModellingPage.tab.productDetailPage',
            },
          },
          {
            path: 'switching-modelling',
            name: 'switching-modelling',
            component: SwitchingModelling,
            omitFromNav: true,
            meta: {
              featureKey: 'switchingModellingEnabled',
              requiresPermission: permissions.canViewSwitchingModellingPage,
              titleExtension: 'scenarioModellingPage.tab.switchingModelling',
            },
          },
        ],
      },
      {
        path: 'assortments-panel',
        children: [
          {
            path: '', // will match /assortments-panel as parent
            name: 'assortments-panel',
            component: AssortmentPanel,
            omitFromNav: true,
            meta: {
              requiresPermission: permissions.canViewAssortmentsPanel,
              titleExtension: 'scenarioAssortmentPanel.assortments',
            },
          },
          {
            path: ':canvasId',
            name: 'assortments-canvas',
            component: ScenarioAssortments,
            omitFromNav: true,
            meta: {
              requiresPermission: permissions.canViewAssortmentCanvas,
              titleExtension: 'assortmentCanvasPage.assortments',
            },
          },
        ],
      },
      {
        path: 'extracts',
        name: 'extracts',
        component: ScenarioExtracts,
        omitFromNav: true,
        meta: {
          requiresPermission: permissions.canViewExtractsPage,
          titleExtension: 'toolbar.extractsTab',
        },
      },
      executionRoute,
    ],
  },
];

const adminRouteDefinition = {
  path: '/admin',
  name: 'AdminPage',
  id: 'admin',
  component: Admin,
  linkText: 'navBar.admin',
  omitFromNav: false,
  meta: {
    featureKey: 'adminControlsEnabled',
    requiresPermission: permissions.canViewAdminPage,
    titleExtension: 'navBar.admin',
  },
};

const clientAdminRouteDefinition = {
  path: '/client-admin',
  name: 'ClientAdminPage',
  id: 'client-admin',
  component: ClientAdmin,
  linkText: 'navBar.clientAdmin',
  omitFromNav: false,
  meta: {
    featureKey: 'clientAdminControlsEnabled',
    requiresPermission: permissions.canViewClientAdminPage,
    titleExtension: 'navBar.clientAdmin',
  },
};

// We have to get admin controls permissions from user context as it cannot be stored in client config now,
// so just hiding the tab on UI for envs with no permissions
// as user-context is loaded after building the routes
const adminRoutes = [adminRouteDefinition, clientAdminRouteDefinition];

// If the exported routes contains an empty object, it breaks
// We handle combining as arrays instead to avoid this issue
const combinedRoutes = [...baseRoutes, ...adminRoutes];

export default combinedRoutes;
